/* ============================================================
   TransferLabs — Shipping Cutoff Countdown

   Hydrates every element marked [data-tl-ship-countdown] and
   ticks once per second. Cutoff is configured per element via
   data attributes:

     data-tl-ship-countdown
     data-cutoff-hour="11"          (24h, in target tz)
     data-cutoff-minute="0"
     data-tz="America/Los_Angeles"  (IANA name; PST/PDT auto)
     data-skip-weekends="true"      (skip Sat/Sun for cutoff)

   Inside, the snippet exposes hooks the JS writes into:
     [data-tl-ship-time]      → "HH:MM:SS"
     [data-tl-ship-day]       → "tomorrow" | "Monday" | …
     [data-tl-ship-cutoff-h]  → "11am PST" (current tz abbr)
   ============================================================ */

(function () {
  'use strict';

  const DOW_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const DOW_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  function getTimeInTZ(date, tz) {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      weekday: 'short',
    });
    const parts = fmt.formatToParts(date).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
    return {
      y: +parts.year,
      m: +parts.month,
      d: +parts.day,
      h: +parts.hour,
      mi: +parts.minute,
      s: +parts.second,
      dow: DOW_MAP[parts.weekday] ?? 0,
    };
  }

  // Find the UTC instant at which the wall clock in `tz` reads
  // exactly y/m/d hour:minute. Two-pass converges around DST jumps.
  function tzWallToUTC(y, m, d, hour, minute, tz) {
    let guess = Date.UTC(y, m - 1, d, hour, minute);
    for (let i = 0; i < 2; i++) {
      const t = getTimeInTZ(new Date(guess), tz);
      const targetUTC = Date.UTC(y, m - 1, d, hour, minute);
      const tzUTC = Date.UTC(t.y, t.m - 1, t.d, t.h, t.mi);
      const diff = targetUTC - tzUTC;
      if (diff === 0) break;
      guess += diff;
    }
    return guess;
  }

  function getTZAbbreviation(date, tz) {
    try {
      const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' });
      const part = fmt.formatToParts(date).find(p => p.type === 'timeZoneName');
      return part ? part.value : '';
    } catch (e) { return ''; }
  }

  function nextCutoffMs(now, hour, minute, tz, skipWeekends) {
    const t = getTimeInTZ(now, tz);
    let cutoff = tzWallToUTC(t.y, t.m, t.d, hour, minute, tz);
    if (cutoff <= now.getTime()) cutoff += 86_400_000;
    if (skipWeekends) {
      let probe = getTimeInTZ(new Date(cutoff), tz);
      while (probe.dow === 0 || probe.dow === 6) {
        cutoff += 86_400_000;
        probe = getTimeInTZ(new Date(cutoff), tz);
      }
    }
    return cutoff;
  }

  // Delivery is the next business day after the cutoff day. We always
  // surface the SOONEST possible date — i.e. the same-day-production
  // (rush) date — to push customers toward adding Jump the Line, even
  // when rush isn't yet in their cart. The "when ordering Rush active"
  // copy in the snippet keeps the conditionality explicit.
  function deliveryDOW(cutoffMs, tz, skipWeekends) {
    let delivery = cutoffMs + 86_400_000;
    let probe = getTimeInTZ(new Date(delivery), tz);
    while (skipWeekends && (probe.dow === 0 || probe.dow === 6)) {
      delivery += 86_400_000;
      probe = getTimeInTZ(new Date(delivery), tz);
    }
    return { dow: probe.dow, ms: delivery };
  }

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function partsRemaining(ms) {
    if (ms <= 0) return { h: '00', m: '00', s: '00' };
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return { h: pad2(h), m: pad2(m), s: pad2(s) };
  }

  function formatRemaining(ms) {
    const p = partsRemaining(ms);
    return `${p.h}:${p.m}:${p.s}`;
  }

  function formatCutoffLabel(hour, minute, tzAbbr) {
    const period = hour >= 12 ? 'pm' : 'am';
    const h12 = ((hour + 11) % 12) + 1;
    const mm = minute > 0 ? ':' + pad2(minute) : '';
    return `${h12}${mm}${period}${tzAbbr ? ' ' + tzAbbr : ''}`;
  }

  function formatDeliveryDate(deliveryMs, tz) {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).format(new Date(deliveryMs));
    } catch (e) {
      const d = new Date(deliveryMs);
      return d.toDateString().slice(0, 10);
    }
  }

  function formatDeliveryDay(deliveryDow, todayDow, cutoffDow) {
    // Say "tomorrow" only when the customer can still ship today AND
    // delivery is literally the next calendar day. Otherwise name the day.
    const tomorrow = (todayDow + 1) % 7;
    if (cutoffDow === todayDow && deliveryDow === tomorrow) return 'tomorrow';
    return DOW_NAMES[deliveryDow];
  }

  // Track all hydrated elements so cart:update events can refresh them.
  const REGISTERED = new Set();

  function isRushInCart(cart, rushHandles) {
    if (!cart || !Array.isArray(cart.items) || !rushHandles || !rushHandles.length) {
      return false;
    }
    const handleSet = new Set(rushHandles);
    return cart.items.some(item => {
      if (!item) return false;
      if (item.handle && handleSet.has(item.handle)) return true;
      // Last-resort safety net: any line item whose product title
      // starts with "Rush" (case-insensitive) counts as a rush item.
      const title = item.product_title || item.title || '';
      return /^\s*rush\b/i.test(title);
    });
  }

  function applyRushState(el, rushActive) {
    el.dataset.rushActive = rushActive ? 'true' : 'false';
    el.classList.toggle('tl-ship--rush-active', rushActive);
    if (typeof el.__tlShipTick === 'function') el.__tlShipTick();
  }

  function hydrate(el) {
    if (el.dataset.tlShipReady === '1') return;
    el.dataset.tlShipReady = '1';
    REGISTERED.add(el);

    const hour = parseInt(el.dataset.cutoffHour, 10);
    const minute = parseInt(el.dataset.cutoffMinute, 10) || 0;
    const tz = el.dataset.tz || 'America/Los_Angeles';
    const skipWeekends = el.dataset.skipWeekends !== 'false';
    if (!Number.isFinite(hour)) return;

    const $times = el.querySelectorAll('[data-tl-ship-time]');
    const $hs = el.querySelectorAll('[data-tl-ship-h]');
    const $ms = el.querySelectorAll('[data-tl-ship-m]');
    const $ss = el.querySelectorAll('[data-tl-ship-s]');
    const $days = el.querySelectorAll('[data-tl-ship-day]');
    const $dates = el.querySelectorAll('[data-tl-ship-date]');
    const $cutoffs = el.querySelectorAll('[data-tl-ship-cutoff-h]');

    function tick() {
      const now = new Date();
      const cutoffMs = nextCutoffMs(now, hour, minute, tz, skipWeekends);
      const remaining = cutoffMs - now.getTime();
      const today = getTimeInTZ(now, tz);
      const cutoffDay = getTimeInTZ(new Date(cutoffMs), tz);

      // Always surface the rush (same-day production) delivery date —
      // promotional messaging. The snippet's copy says "when ordering
      // Rush active" so the contingency is clear to the customer.
      const delivery = deliveryDOW(cutoffMs, tz, skipWeekends);

      const dayLabel = formatDeliveryDay(delivery.dow, today.dow, cutoffDay.dow);
      const dateLabel = formatDeliveryDate(delivery.ms, tz);
      const tzAbbr = getTZAbbreviation(now, tz);

      const parts = partsRemaining(remaining);
      const timeStr = `${parts.h}:${parts.m}:${parts.s}`;
      const cutoffStr = formatCutoffLabel(hour, minute, tzAbbr);
      $times.forEach(n => { n.textContent = timeStr; });
      $hs.forEach(n => { n.textContent = parts.h; });
      $ms.forEach(n => { n.textContent = parts.m; });
      $ss.forEach(n => { n.textContent = parts.s; });
      $days.forEach(n => { n.textContent = dayLabel; });
      $dates.forEach(n => { n.textContent = dateLabel; });
      $cutoffs.forEach(n => { n.textContent = cutoffStr; });
    }

    el.__tlShipTick = tick;
    tick();
    // Align the next tick to the next wall second so the number flips cleanly.
    const drift = 1000 - (Date.now() % 1000);
    setTimeout(() => {
      tick();
      setInterval(tick, 1000);
    }, drift);
  }

  function init() {
    document.querySelectorAll('[data-tl-ship-countdown]').forEach(hydrate);
  }

  // Refresh rush state on cart updates. Prefer the cart resource carried
  // on the event detail to avoid an extra fetch; fall back to /cart.js
  // if the dispatcher didn't include it.
  async function handleCartUpdate(event) {
    if (REGISTERED.size === 0) return;

    let cart = event && event.detail && event.detail.resource;
    if (!cart || !Array.isArray(cart.items)) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        cart = await res.json();
      } catch (e) {
        return;
      }
    }

    REGISTERED.forEach(el => {
      const handles = (el.dataset.rushHandles || el.dataset.rushHandle || '')
        .split(',')
        .map(h => h.trim())
        .filter(Boolean);
      if (!handles.length) return;
      const active = isRushInCart(cart, handles);
      if ((el.dataset.rushActive === 'true') !== active) {
        applyRushState(el, active);
      }
    });
  }

  document.addEventListener('cart:update', handleCartUpdate);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
